// Mock fixtures used when REACT_APP_API_URL is empty (dev / preview).
// Designed to look realistic so screens populate end-to-end.

export const MOCK_LATENCY = 220;

const now = new Date();
const daysAgo = (n) => new Date(now.getTime() - n * 86400000).toISOString();

let _collections = [
    { id: "c-default", name: "Default collection", isDefault: true },
    { id: "c-suvs", name: "SUVs under €15k", isDefault: false },
    { id: "c-mom", name: "Cars for Mom", isDefault: false },
];

const shareState = {
    "c-suvs": { isShared: true, token: "shr_AbC123dEf456" },
};

function mk(
    id,
    make,
    model,
    year,
    mileage,
    price,
    fuel,
    trans,
    score,
    verdict,
    daysOld,
    collectionId = "c-default",
    notes = "",
) {
    const c = _collections.find((x) => x.id === collectionId);
    return {
        id,
        url: `https://autoscout24.de/offer/${id}`,
        make,
        model,
        year,
        mileage,
        mileageUnit: "km",
        price,
        currency: "EUR",
        fuelType: fuel,
        transmission: trans,
        score,
        verdict,
        createdAt: daysAgo(daysOld),
        notes,
        collectionId,
        collectionName: c?.name || "Default collection",
    };
}

let _items = [
    mk("a1", "Volkswagen", "Golf", 2018, 124500, 14900, "diesel", "manual", 82, "good", 1, "c-default", "Service book complete — ask about DSG."),
    mk("a2", "Skoda", "Octavia RS", 2019, 96000, 22400, "diesel", "automatic", 76, "caution", 2, "c-default"),
    mk("a3", "BMW", "320d", 2017, 142000, 18900, "diesel", "automatic", 64, "caution", 3, "c-suvs"),
    mk("a4", "Peugeot", "508", 2020, 58000, 18700, "diesel", "automatic", 88, "good", 4, "c-default"),
    mk("a5", "Toyota", "RAV4", 2020, 45000, 25800, "hybrid", "automatic", 91, "good", 5, "c-suvs"),
    mk("a6", "Audi", "A4 Avant", 2016, 188000, 13400, "diesel", "automatic", 48, "avoid", 7, "c-default", "Rust on rear arches."),
    mk("a7", "Volkswagen", "Passat", 2015, 220000, 8900, "diesel", "manual", 41, "avoid", 9, "c-default"),
    mk("a8", "Ford", "Focus", 2019, 72000, 13200, "petrol", "manual", 78, "good", 11, "c-mom"),
    mk("a9", "Mazda", "CX-5", 2021, 38000, 27500, "petrol", "automatic", 85, "good", 13, "c-suvs"),
    mk("a10", "Hyundai", "Tucson", 2020, 64000, 19900, "hybrid", "automatic", 80, "good", 16, "c-suvs"),
    mk("a11", "Renault", "Megane", 2017, 98000, 9800, "petrol", "manual", 67, "caution", 22, "c-mom"),
    mk("a12", "Volvo", "XC60", 2019, 88000, 31400, "diesel", "automatic", 83, "good", 30, "c-suvs"),
];

function buildDetailed(base) {
    const verdict = base.verdict;
    return {
        ...base,
        summary: `${base.year} ${base.make} ${base.model} with ${base.mileage.toLocaleString()} km. ${verdict === "good" ? "Strong candidate — service history checks out and price tracks the market." : verdict === "caution" ? "Decent car but high mileage / model-specific risks. Inspect carefully before committing." : "Multiple red flags. Walk away unless price drops significantly."}`,
        pros:
            verdict === "avoid"
                ? ["Asking price below market", "Includes winter tyres"]
                : [
                      "Full service book",
                      "Single private owner",
                      "Recent timing belt service",
                      "Below-market asking price",
                  ],
        cons:
            verdict === "good"
                ? ["Tyres at 30% life", "1 open recall (airbag)"]
                : [
                      "Above-average mileage for year",
                      "EA288 — DPF clogging common after 120k",
                      "No mention of timing belt service",
                  ],
        modelInsight: `The ${base.make} ${base.model} (${base.year}) is generally a robust platform, but ${base.fuelType === "diesel" ? "diesel variants" : "this variant"} can suffer from ${base.fuelType === "diesel" ? "DPF clogging and EGR valve issues after 120,000 km" : "ignition coil failures and minor electrical gremlins"} — confirm service history.`,
        mileageNote:
            base.mileage > 150000
                ? "Above average for the model year."
                : "In line with model-year expectations.",
        redFlags:
            verdict === "avoid"
                ? ["Mileage rolled back at last inspection", "2 open recalls"]
                : [],
        greenFlags: ["Vehicle registered in same country since new"],
        creditsRemaining: 12,
        fromCache: false,
        nhtsaComplaints: verdict === "avoid" ? 147 : 42,
        nhtsaCrashes: verdict === "avoid" ? 8 : 3,
        nhtsaRecalls: verdict === "avoid" ? 3 : 1,
        nhtsaRecallsParkIt: false,
        nhtsaComponentBreakdown: {
            ENGINE: 15,
            ELECTRICAL: 8,
            "FUEL SYSTEM": 6,
        },
        nhtsaRecallSummaries: [
            "Airbag inflator may rupture under high humidity conditions.",
            "Fuel pump impeller may swell, causing engine stall.",
        ],
        detailedReport: {
            maintenanceForecast: [
                {
                    item: "Timing belt replacement",
                    urgency: "upcoming",
                    estimatedCost: "€600–900",
                    mileageInterval: "Every 210,000 km",
                },
                {
                    item: "Front brake pads",
                    urgency: "routine",
                    estimatedCost: "€180–240",
                    mileageInterval: "Every 60,000 km",
                },
                {
                    item: "DPF clean",
                    urgency: "long-term",
                    estimatedCost: "€280–360",
                    mileageInterval: "As needed",
                },
                {
                    item: "Full transmission service",
                    urgency: "upcoming",
                    estimatedCost: "€420",
                    mileageInterval: "Every 80,000 km",
                },
            ],
            marketPriceAnalysis: {
                assessment:
                    verdict === "avoid"
                        ? "above"
                        : verdict === "good"
                          ? "below"
                          : "fair",
                percentageVsMarket:
                    verdict === "avoid" ? 8 : verdict === "good" ? -6 : -1,
                explanation:
                    verdict === "avoid"
                        ? "Asking 8% above median for comparable listings."
                        : verdict === "good"
                          ? "Asking 6% below median — strong value."
                          : "Within ±2% of market median.",
                comparableListingsFound: 12,
                medianMarketPrice: Math.round(base.price * 1.04),
                marketCurrency: "EUR",
                searchUrl: "https://autoscout24.de/lst/...",
                referenceSites: [
                    "autoscout24.de",
                    "mobile.de",
                    "otomoto.pl",
                    "marktplaats.nl",
                ],
            },
            negotiationTips: [
                "Timing belt service due → ask for €480 reduction or completed service before purchase.",
                "Recent reports of EA288 EGR issues — request a cold-start demonstration.",
                "Average asking is €" +
                    Math.round(base.price * 1.04).toLocaleString() +
                    " — anchor your first offer 8% below their ask.",
                "Two open recalls — confirm in writing the seller will complete them pre-handover.",
            ],
            inspectionChecklist: [
                "Cold start engine — listen for diesel knock and irregular idle",
                "Check DPF regeneration log via OBD scan",
                "Inspect underbody rust, especially rear wheel arches and subframe",
                "Test DSG/automatic shift quality through all gears",
                "Verify tyre tread depth and brand consistency (mismatched = recent cheap replacement)",
                "Confirm VIN matches title and inspection report",
                "Test all electrical functions: windows, mirrors, infotainment, A/C",
            ],
        },
        odometerRecords: [
            {
                date: daysAgo(420),
                type: "Periodic inspection",
                mileage: Math.round(base.mileage * 0.6),
                result: "passed",
                nextInspection: daysAgo(-310),
            },
            {
                date: daysAgo(40),
                type: "Pre-sale check",
                mileage: base.mileage,
                result: "passed",
                nextInspection: daysAgo(-690),
            },
        ],
        odometerFraudDetected: verdict === "avoid",
        odometerFraudDetails:
            verdict === "avoid"
                ? "Inspection 2 years ago reported 165,000 km; current listing shows 142,000 km."
                : null,
        odometerVehicleName: `${base.make.toUpperCase()} ${base.model.toUpperCase()}`,
        odometerLastMileage: base.mileage,
        odometerNextInspection: daysAgo(-310),
        odometerDaysUntilInspection: 310,
    };
}

const profile = {
    country: "CZ",
    locale: "en",
};

let credits = 12;

let bulkJob = null;
let bulkInterval = null;
function startBulkJob(urls, collectionId) {
    const job = {
        jobId: `job-${Date.now()}`,
        status: "processing",
        totalUrls: urls.length,
        processedCount: 0,
        urls,
        results: [],
        _collectionId: collectionId,
    };
    bulkJob = job;
    if (bulkInterval) clearInterval(bulkInterval);
    bulkInterval = setInterval(() => {
        if (!bulkJob) return;
        if (bulkJob.processedCount >= bulkJob.totalUrls) {
            bulkJob.status = "completed";
            clearInterval(bulkInterval);
            bulkInterval = null;
            return;
        }
        const url = bulkJob.urls[bulkJob.processedCount];
        const id = `bulk-${bulkJob.processedCount}-${Date.now()}`;
        const verdicts = ["good", "caution", "avoid"];
        const v = verdicts[bulkJob.processedCount % 3];
        const score = v === "good" ? 84 : v === "caution" ? 68 : 46;
        bulkJob.results.push({
            url,
            success: true,
            listingId: id,
            make: ["Volkswagen", "Skoda", "BMW", "Toyota"][
                bulkJob.processedCount % 4
            ],
            model: ["Golf", "Octavia", "320d", "RAV4"][
                bulkJob.processedCount % 4
            ],
            year: 2018 + (bulkJob.processedCount % 4),
            score,
            verdict: v,
        });
        bulkJob.processedCount++;
    }, 1200);
    return job;
}

function match(pattern, url) {
    const re = new RegExp(
        "^" +
            pattern.replace(/:[a-zA-Z]+/g, "([^/?]+)").replace(/\//g, "\\/") +
            "(?:\\?.*)?$",
    );
    const m = url.match(re);
    return m ? m.slice(1) : null;
}

export const mockHandlers = [
    // ---- analyse ----
    {
        match: (m, u) => m === "POST" && u.startsWith("/api/analyse/bulk") && !u.includes("/active") && !u.match(/\/api\/analyse\/bulk\/[^/]+$/),
        run: ({ body }) => {
            const job = startBulkJob(
                body.urls || [],
                body.collectionId || "c-default",
            );
            return {
                jobId: job.jobId,
                totalUrls: job.totalUrls,
                status: "pending",
            };
        },
    },
    {
        match: (m, u) => m === "GET" && u === "/api/analyse/bulk/active",
        run: () => bulkJob || { __status: 204, error: null },
    },
    {
        match: (m, u) =>
            m === "GET" && match("/api/analyse/bulk/:id", u),
        run: () => bulkJob || { __status: 404, error: "Job not found" },
    },
    {
        match: (m, u) => m === "POST" && u === "/api/analyse",
        run: ({ body }) => {
            if (credits <= 0)
                return { __status: 402, error: "Insufficient credits" };
            credits--;
            const id = `a-${Date.now()}`;
            const base = mk(
                id,
                "Volkswagen",
                "Golf",
                2019,
                98000,
                15500,
                "diesel",
                "manual",
                81,
                "good",
                0,
                body?.collectionId || "c-default",
            );
            base.url = body?.url || base.url;
            _items.unshift(base);
            return buildDetailed(base);
        },
    },
    {
        match: (m, u) => m === "POST" && u === "/api/analyse/dispute",
        run: () => ({ message: "Thank you for your feedback" }),
    },
    // ---- history ----
    {
        match: (m, u) => m === "GET" && u.startsWith("/api/history/by-url"),
        run: ({ params }) => {
            const found = _items.find((i) => i.url === params?.url);
            return found || { __status: 404, error: "Not found" };
        },
    },
    {
        match: (m, u) => m === "GET" && u.startsWith("/api/history/batch"),
        run: ({ params }) => {
            const ids = (params?.ids || "").split(",").filter(Boolean);
            return _items
                .filter((i) => ids.includes(i.id))
                .map((i) => buildDetailed(i));
        },
    },
    {
        match: (m, u) => m === "GET" && match("/api/history/:id", u),
        run: ({ url }) => {
            const id = match("/api/history/:id", url)[0];
            const item = _items.find((i) => i.id === id);
            return item
                ? buildDetailed(item)
                : { __status: 404, error: "Not found" };
        },
    },
    {
        match: (m, u) =>
            m === "PATCH" && match("/api/history/:id", u),
        run: ({ url, body }) => {
            const id = match("/api/history/:id", url)[0];
            const item = _items.find((i) => i.id === id);
            if (!item) return { __status: 404, error: "Not found" };
            if (body.notes !== undefined) item.notes = body.notes;
            if (body.price !== undefined) item.price = body.price;
            if (body.mileage !== undefined) item.mileage = body.mileage;
            if (body.collectionId !== undefined) {
                item.collectionId = body.collectionId;
                item.collectionName =
                    _collections.find((c) => c.id === body.collectionId)
                        ?.name || "Default collection";
            }
            return {
                price: item.price,
                mileage: item.mileage,
                notes: item.notes,
            };
        },
    },
    {
        match: (m, u) =>
            m === "DELETE" && match("/api/history/:id", u),
        run: ({ url }) => {
            const id = match("/api/history/:id", url)[0];
            _items = _items.filter((i) => i.id !== id);
            return null;
        },
    },
    {
        match: (m, u) => m === "GET" && u.startsWith("/api/history"),
        run: ({ params = {} }) => {
            let list = _items.slice();
            if (params.verdict) {
                const v = params.verdict.split(",");
                list = list.filter((i) => v.includes(i.verdict));
            }
            if (params.collections) {
                const cs = params.collections.split(",");
                list = list.filter((i) => cs.includes(i.collectionId));
            }
            if (params.scoreMin)
                list = list.filter((i) => i.score >= +params.scoreMin);
            if (params.scoreMax)
                list = list.filter((i) => i.score <= +params.scoreMax);
            if (params.priceMin)
                list = list.filter((i) => i.price >= +params.priceMin);
            if (params.priceMax)
                list = list.filter((i) => i.price <= +params.priceMax);
            if (params.mileageMin)
                list = list.filter((i) => i.mileage >= +params.mileageMin);
            if (params.mileageMax)
                list = list.filter((i) => i.mileage <= +params.mileageMax);
            if (params.yearMin)
                list = list.filter((i) => i.year >= +params.yearMin);
            if (params.yearMax)
                list = list.filter((i) => i.year <= +params.yearMax);
            if (params.fuelType) {
                const fs = params.fuelType.split(",");
                list = list.filter((i) => fs.includes(i.fuelType));
            }
            if (params.transmission) {
                const ts = params.transmission.split(",");
                list = list.filter((i) => ts.includes(i.transmission));
            }
            if (params.make) {
                const q = params.make.toLowerCase();
                list = list.filter(
                    (i) =>
                        (i.make + " " + i.model).toLowerCase().includes(q),
                );
            }
            const sortBy = params.sortBy || "date";
            const dir = params.sortDir === "asc" ? 1 : -1;
            list.sort((a, b) => {
                const key =
                    sortBy === "date"
                        ? "createdAt"
                        : sortBy === "make"
                          ? "make"
                          : sortBy;
                if (a[key] < b[key]) return -1 * dir;
                if (a[key] > b[key]) return 1 * dir;
                return 0;
            });
            const page = Math.max(1, +params.page || 1);
            const pageSize = Math.min(100, Math.max(1, +params.pageSize || 20));
            const start = (page - 1) * pageSize;
            return {
                items: list.slice(start, start + pageSize),
                total: list.length,
                page,
                pageSize,
            };
        },
    },
    // ---- collections ----
    {
        match: (m, u) =>
            m === "POST" && match("/api/collections/:id/share", u),
        run: ({ url }) => {
            const id = match("/api/collections/:id/share", url)[0];
            const token =
                "shr_" + Math.random().toString(36).slice(2, 14);
            shareState[id] = { isShared: true, token };
            return { token, url: `/shared/${token}` };
        },
    },
    {
        match: (m, u) =>
            m === "GET" && match("/api/collections/:id/share", u),
        run: ({ url }) => {
            const id = match("/api/collections/:id/share", url)[0];
            return shareState[id] || { isShared: false, token: null };
        },
    },
    {
        match: (m, u) =>
            m === "DELETE" && match("/api/collections/:id/share", u),
        run: ({ url }) => {
            const id = match("/api/collections/:id/share", url)[0];
            delete shareState[id];
            return null;
        },
    },
    {
        match: (m, u) => m === "GET" && u === "/api/collections",
        run: () =>
            _collections.map((c) => ({
                ...c,
                itemCount: _items.filter((i) => i.collectionId === c.id).length,
                isShared: !!shareState[c.id]?.isShared,
                shareToken: shareState[c.id]?.token || null,
            })),
    },
    {
        match: (m, u) => m === "POST" && u === "/api/collections",
        run: ({ body }) => {
            const id =
                "c-" + Math.random().toString(36).slice(2, 8);
            const col = { id, name: body.name, isDefault: false };
            _collections.push(col);
            return { ...col, itemCount: 0, isShared: false, shareToken: null };
        },
    },
    {
        match: (m, u) =>
            m === "PATCH" && match("/api/collections/:id", u),
        run: ({ url, body }) => {
            const id = match("/api/collections/:id", url)[0];
            const c = _collections.find((x) => x.id === id);
            if (!c) return { __status: 404, error: "Not found" };
            if (c.isDefault)
                return { __status: 400, error: "Cannot rename default" };
            c.name = body.name;
            return c;
        },
    },
    {
        match: (m, u) =>
            m === "DELETE" && match("/api/collections/:id", u),
        run: ({ url }) => {
            const id = match("/api/collections/:id", url)[0];
            const c = _collections.find((x) => x.id === id);
            if (!c) return { __status: 404, error: "Not found" };
            if (c.isDefault)
                return { __status: 400, error: "Cannot delete default" };
            _items = _items.map((i) =>
                i.collectionId === id
                    ? { ...i, collectionId: "c-default", collectionName: "Default collection" }
                    : i,
            );
            _collections = _collections.filter((x) => x.id !== id);
            return null;
        },
    },
    // ---- shared (public) ----
    {
        match: (m, u) =>
            m === "GET" && match("/api/shared/:token/:id", u),
        run: ({ url }) => {
            const [token, id] = match("/api/shared/:token/:id", url);
            const found = Object.entries(shareState).find(
                ([, v]) => v.token === token,
            );
            if (!found) return { __status: 404, error: "Not found" };
            const item = _items.find((i) => i.id === id);
            return item ? buildDetailed(item) : { __status: 404, error: "Not found" };
        },
    },
    {
        match: (m, u) => m === "GET" && match("/api/shared/:token", u),
        run: ({ url }) => {
            const token = match("/api/shared/:token", url)[0];
            const found = Object.entries(shareState).find(
                ([, v]) => v.token === token,
            );
            if (!found) return { __status: 404, error: "Not found" };
            const colId = found[0];
            const col = _collections.find((c) => c.id === colId);
            return {
                name: col?.name || "Shared collection",
                analyses: _items
                    .filter((i) => i.collectionId === colId)
                    .map(buildDetailed),
            };
        },
    },
    // ---- credits ----
    {
        match: (m, u) => m === "GET" && u === "/api/credits/balance",
        run: () => ({ credits }),
    },
    {
        match: (m, u) => m === "POST" && u === "/api/credits/checkout",
        run: ({ body }) => ({
            clientSecret: `cs_test_mock_${body.packageId}_${Date.now()}`,
        }),
    },
    {
        match: (m, u) => m === "GET" && u.startsWith("/api/credits/session-status"),
        run: ({ params }) => {
            // mock: granting 15 credits
            credits += 15;
            return { status: "complete", customerEmail: "you@spotcar.dev", sessionId: params?.session_id };
        },
    },
    // ---- profile ----
    {
        match: (m, u) => m === "GET" && u === "/api/profile",
        run: () => ({ ...profile }),
    },
    {
        match: (m, u) => m === "PUT" && u === "/api/profile",
        run: ({ body }) => {
            if (body.country !== undefined) profile.country = body.country;
            if (body.locale !== undefined) profile.locale = body.locale;
            return { ...profile };
        },
    },
    {
        match: (m, u) => m === "DELETE" && u === "/api/profile",
        run: () => null,
    },
    {
        match: (m, u) => m === "GET" && u === "/api/profile/export",
        run: () => ({
            profile: {
                id: "u-mock",
                email: "you@spotcar.dev",
                ...profile,
                credits,
                acceptedTermsAt: daysAgo(30),
                acceptedPrivacyAt: daysAgo(30),
                createdAt: daysAgo(90),
            },
            analyses: _items,
            transactions: [
                {
                    id: "t1",
                    package: "standard",
                    amount: 4.99,
                    currency: "EUR",
                    credits: 15,
                    createdAt: daysAgo(14),
                },
            ],
            feedbacks: [],
            disputes: [],
        }),
    },
    {
        match: (m, u) =>
            m === "POST" && (u === "/api/profile/consent" || u === "/api/feedback"),
        run: () => ({ ok: true }),
    },
    {
        match: (m, u) => m === "DELETE" && u === "/api/profile/consent",
        run: () => ({ ok: true }),
    },
];
